// ═══════════════════════════════════════════════════════════════
// orgModel.ts – a szervezeti struktúra közös modellje (kezelőfelület és nyilvános oldal)
// egység → (alá: beosztott egységek, mellé: törzskari egységek) → pozíciók → munkatársak
// ═══════════════════════════════════════════════════════════════
import type { OrgUnit, Person, Position, PublicPersonnelData, OrgChartSettings } from '../../types';
import { naturalCompare } from '../../utils/sort';

export interface OrgPerson {
  id: number;
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  room?: string | null;
  org_unit_id?: number | null;
  position_ids: number[];
  raw?: Person;
}
export interface OrgPosition {
  id: number;
  org_unit_id: number | null;
  title: string;
  is_leader: number;
  sort_order: number;
  job_description?: string;
  multi_person?: number;
  raw?: Position;
}
export interface OrgModel {
  units: OrgUnit[];
  positions: OrgPosition[];
  persons: OrgPerson[];
}

export const ORG_CHART_DEFAULTS: OrgChartSettings = { orientation: 'vertical', detail: 'leaders', root_title: '' };

export function fromOverview(units: OrgUnit[], positions: Position[], persons: Person[], assignments: { person_id: number; position_id: number }[] = []): OrgModel {
  return {
    units: units.map((u) => ({ ...u, parent_id: u.parent_id ?? null, placement: u.placement === 'beside' ? 'beside' : 'below' })),
    positions: positions.map((p) => ({ id: p.id, org_unit_id: p.org_unit_id ?? null, title: p.title, is_leader: p.is_leader ? 1 : 0, sort_order: p.sort_order || 0, job_description: p.job_description, multi_person: p.multi_person, raw: p })),
    persons: persons.map((p) => {
      const ids = assignments.filter((a) => a.person_id === p.id).map((a) => a.position_id);
      if (p.position_id && !ids.includes(Number(p.position_id))) ids.push(Number(p.position_id));
      return { id: p.id, name: p.name, title: p.title, email: p.email, phone: p.phone, room: p.room, org_unit_id: p.org_unit_id ?? null, position_ids: ids, raw: p };
    }),
  };
}

export function fromPublic(d: PublicPersonnelData): OrgModel {
  return {
    units: d.units.map((u) => ({ ...u, parent_id: u.parent_id ?? null, placement: u.placement === 'beside' ? 'beside' : 'below' })),
    positions: d.positions.map((p) => ({ ...p, org_unit_id: p.org_unit_id ?? null })),
    persons: d.persons.map((p) => ({ ...p, position_ids: p.position_ids || [] })),
  };
}

const byOrder = <T extends { sort_order?: number; name?: string; title?: string }>(a: T, b: T) =>
  ((a.sort_order || 0) - (b.sort_order || 0)) || naturalCompare(a.name || a.title || '', b.name || b.title || '');

/** közvetlen alárendeltek; placement megadásával csak a beosztott (below) vagy a törzskari (beside) egységek */
export function childUnits(m: OrgModel, parentId: number | null, placement?: 'below' | 'beside'): OrgUnit[] {
  const known = new Set(m.units.map((u) => u.id));
  return m.units
    .filter((u) => {
      const pid = u.parent_id && known.has(u.parent_id) ? u.parent_id : null; // ismeretlen szülő → legfelső szint
      return pid === parentId && (!placement || (u.placement || 'below') === placement);
    })
    .sort(byOrder);
}

export function unitPositions(m: OrgModel, unitId: number | null): OrgPosition[] {
  return m.positions.filter((p) => (p.org_unit_id ?? null) === unitId).sort((a, b) => (b.is_leader - a.is_leader) || byOrder(a, b));
}

export function positionPersons(m: OrgModel, posId: number): OrgPerson[] {
  return m.persons.filter((p) => p.position_ids.includes(posId)).sort((a, b) => naturalCompare(a.name, b.name));
}

/** az egységhez közvetlenül (pozíció nélkül vagy más egység pozíciójával) rendelt munkatársak */
export function unitLoosePersons(m: OrgModel, unitId: number | null): OrgPerson[] {
  const posUnit = new Map(m.positions.map((p) => [p.id, p.org_unit_id ?? null]));
  return m.persons
    .filter((p) => {
      const inUnitPos = p.position_ids.some((id) => posUnit.get(id) === unitId && posUnit.has(id));
      if (inUnitPos) return false;
      if (unitId === null) return !p.org_unit_id && !p.position_ids.some((id) => posUnit.has(id) && posUnit.get(id) !== null);
      return (p.org_unit_id ?? null) === unitId;
    })
    .sort((a, b) => naturalCompare(a.name, b.name));
}

export function unitLeaders(m: OrgModel, unitId: number): { position: OrgPosition; persons: OrgPerson[] }[] {
  return unitPositions(m, unitId).filter((p) => p.is_leader).map((position) => ({ position, persons: positionPersons(m, position.id) }));
}

export function unitStaffCount(m: OrgModel, unitId: number): number {
  const ids = new Set<number>();
  for (const p of unitPositions(m, unitId)) for (const pr of positionPersons(m, p.id)) ids.add(pr.id);
  for (const pr of unitLoosePersons(m, unitId)) ids.add(pr.id);
  return ids.size;
}

export function descendantIds(m: OrgModel, unitId: number): Set<number> {
  const out = new Set<number>([unitId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const u of m.units) if (u.parent_id && out.has(u.parent_id) && !out.has(u.id)) { out.add(u.id); grew = true; }
  }
  return out;
}

/** az egység elérési útja (pl. „Hivatal › Pénzügyi Osztály”) */
export function unitPath(m: OrgModel, unitId: number | null | undefined): string {
  const parts: string[] = [];
  let cur = m.units.find((u) => u.id === unitId);
  let guard = 0;
  while (cur && guard++ < 20) {
    parts.unshift(cur.name);
    cur = cur.parent_id ? m.units.find((u) => u.id === cur!.parent_id) : undefined;
  }
  return parts.join(' › ');
}
